/**
 * @file 积分不足场景 key
 * @description 标识触发积分不足弹窗的场景，用作埋点上报。分两类：
 *
 * 一、后置拦截（响应业务码 20035）：由 `buildPointsSceneKey` 从响应推导，
 * 规则为请求路径 + 子场景后缀，后缀取值优先级为
 * ① 请求体 type → ② 响应 data.generatorType → ③ 无后缀，命中即返回、互斥。
 * 统一去掉路径前导 `/`，抹平各处 endpoint 常量的写法差异。
 *
 * 二、前置拦截：走 `POINTS_SCENE_KEY` 显式登记
 */

import {CREATION_CARD_TYPE} from '@/services/home/constants';

export const POINTS_SCENE_KEY = {
    /** 首页创作入口卡片 */
    ...CREATION_CARD_TYPE,
} as const;

export type PointsSceneKey = (typeof POINTS_SCENE_KEY)[keyof typeof POINTS_SCENE_KEY];

const UNKNOWN_SCENE = 'unknown';
const SCENE_TYPE_FIELD = 'type';

const exists = (value: unknown): boolean => value !== null && value !== undefined && value !== '';
const normalizePath = (url?: string): string => (url ?? '').split('?')[0].replace(/^\/+/, '');

const safeParseJson = (raw: string): unknown => {
    try {
        return JSON.parse(raw);
    } catch {
        return null;
    }
};

/**
 * 读取请求体中的 type
 */
const readRequestType = (data: unknown): unknown => {
    const body = typeof data === 'string' ? safeParseJson(data) : data;
    if (body && typeof body === 'object') {
        return (body as Record<string, unknown>)[SCENE_TYPE_FIELD];
    }
    return null;
};

export interface PointsSceneSource {
    /** 请求路径 */
    url?: string;
    /** 请求体 */
    requestData?: unknown;
    /** 响应体的 data */
    responseData?: unknown;
}

export const buildPointsSceneKey = (source: PointsSceneSource): string => {
    const path = normalizePath(source.url);
    if (!path) return UNKNOWN_SCENE;

    const requestType = readRequestType(source.requestData);
    const generatorType = (source.responseData as {generatorType?: unknown} | null | undefined)
        ?.generatorType;
    const suffix = exists(requestType) ? requestType : generatorType;

    return exists(suffix) ? `${path}_${String(suffix)}` : path;
};
